import React, { useMemo, useState } from "react";
import { BsSearch } from "react-icons/bs";
import { FaTimes } from "react-icons/fa";

import { Avatar, IconButton, Input } from "../../ui";
import { MENU_GROUPS } from "./menu";
import SidebarSection from "./SidebarSection";
import { CATEGORY_ORDER } from "./types";
import type { Category, MenuGroup, Role } from "./types";
import { useAdminContext } from "../../context/admin/AdminContext";
import {
  clearBreadCumb,
  resetBreadCumb,
} from "../../lib/breadCrumb/BreadCrumb";
import { clearRoutingHistory } from "../../lib";

type Props = {
  onClose: () => void;
};

const matchesQuery = (group: MenuGroup, q: string) => {
  if (!q) return true;
  const titleMatch = group.title.toLowerCase().includes(q);
  const childMatch = group.menu.some((m) =>
    m.title.toLowerCase().includes(q),
  );
  return titleMatch || childMatch;
};

const prettifyRole = (role?: string) =>
  (role ?? "").replace(/_/g, " ").toLowerCase();

const SidebarContent: React.FC<Props> = ({ onClose }) => {
  const {
    sysAdmin,
    selectedMenu,
    setSelectedMenu,
    setSingleAdmin,
  } = useAdminContext();

  const role = sysAdmin?.role as Role | undefined;
  const firstName: string | undefined = sysAdmin?.first_name;
  const lastName: string | undefined = sysAdmin?.last_name;

  const [query, setQuery] = useState("");

  const visibleGroups = useMemo(() => {
    if (!role) return [] as MenuGroup[];
    const q = query.trim().toLowerCase();
    return MENU_GROUPS.filter(
      (g) => g.roles.includes(role) && matchesQuery(g, q),
    );
  }, [role, query]);

  const groupedByCategory = useMemo(() => {
    const buckets = new Map<Category, MenuGroup[]>();
    for (const category of CATEGORY_ORDER) buckets.set(category, []);
    for (const group of visibleGroups) {
      buckets.get(group.category)?.push(group);
    }
    return CATEGORY_ORDER.map((category) => ({
      category,
      groups: buckets.get(category) ?? [],
    })).filter((b) => b.groups.length > 0);
  }, [visibleGroups]);

  // Preserve legacy "first group open by default" when selectedMenu still holds
  // the numeric-0 initial value from AdminContext. After first click it becomes
  // the group title — stable across filter changes.
  const effectiveOpen =
    typeof selectedMenu === "string"
      ? selectedMenu
      : visibleGroups[0]?.title ?? null;

  const handleToggle = (title: string) => {
    setSelectedMenu(effectiveOpen === title ? null : title);
  };

  const handleNavigate = () => {
    clearBreadCumb();
    resetBreadCumb();
    setSingleAdmin(null);
    clearRoutingHistory();
  };

  const fullName = [firstName, lastName].filter(Boolean).join(" ");
  const prettyRole = prettifyRole(role);

  return (
    <>
      <header className="flex items-center justify-between gap-3 px-5 pb-4 pt-5">
        <div className="flex min-w-0 items-center gap-2.5">
          <span
            aria-hidden
            className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand/10 ring-1 ring-inset ring-brand/20"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-brand" />
          </span>
          <div className="flex min-w-0 flex-col leading-tight">
            <h1 className="truncate text-[15px] font-semibold tracking-tight text-gray-900">
              DoseeAfrica
            </h1>
            <span className="truncate text-[11px] text-gray-500">
              Admin console
            </span>
          </div>
        </div>
        <IconButton
          aria-label="Close sidebar"
          onClick={onClose}
          className="xl:hidden"
          variant="ghost"
          size="sm"
        >
          <FaTimes className="h-3.5 w-3.5" />
        </IconButton>
      </header>

      <div className="px-4 pb-3">
        <Input
          type="text"
          value={query}
          placeholder="Search menu…"
          onChange={(e) => setQuery(e.target.value)}
          leadingIcon={<BsSearch className="h-3.5 w-3.5" />}
          className="h-9 text-[13px]"
        />
      </div>

      <div
        className="sidebar-scroll flex-1 overflow-y-auto overflow-x-hidden px-2 pb-4"
        style={{ scrollbarGutter: "stable both-edges" }}
      >
        {role ? (
          <nav className="flex flex-col">
            {groupedByCategory.map(({ category, groups }, idx) => (
              <section
                key={category}
                className={idx === 0 ? "" : "mt-4"}
                aria-label={category}
              >
                <div className="px-3 pb-1.5 pt-1">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-gray-400">
                    {category}
                  </span>
                </div>
                <div className="flex flex-col">
                  {groups.map((group) => (
                    <SidebarSection
                      key={group.title}
                      group={group}
                      role={role}
                      open={effectiveOpen === group.title}
                      onToggle={() => handleToggle(group.title)}
                      onNavigate={handleNavigate}
                    />
                  ))}
                </div>
              </section>
            ))}

            {groupedByCategory.length === 0 ? (
              <div className="px-3 py-6 text-center text-[12px] text-gray-400">
                No matches for “{query}”.
              </div>
            ) : null}
          </nav>
        ) : null}
      </div>

      <footer className="flex items-center gap-2.5 border-t border-gray-100 px-4 py-3">
        <Avatar firstName={firstName} lastName={lastName} size="sm" />
        <div className="flex min-w-0 flex-col leading-tight">
          <span
            className="truncate text-[12.5px] font-medium text-gray-900"
            title={fullName || undefined}
          >
            {fullName || "—"}
          </span>
          <span className="truncate text-[10.5px] capitalize text-gray-500">
            {prettyRole || "member"}
          </span>
        </div>
      </footer>
    </>
  );
};

export default SidebarContent;
